/**
 * UI-8d Task 6 — PlatformChangeLogPage.
 *
 * Read-only viewer for the SOX-ITGC change-management log
 * (`/platform/change-log`).
 *
 * Composition:
 *   - <RequirePermission permission="platform:audit_view"> gate. Same
 *     grant as the platform audit log (single permission for the two
 *     viewing surfaces).
 *   - PageHeader "Change Management Log".
 *   - Inline filter row (simpler than the audit-log sidebar; the filter
 *     fields differ): changeType / requestedBy / approvedBy as free-text
 *     InputText controls. Updates trigger a refetch via the hook.
 *   - PrimeReact DataTable, virtualized: columns are Implemented,
 *     Type, Description (truncated to 80 chars), Requested by
 *     (first 8 hex), Approved by (first 8 hex or '—').
 *   - Row click → PlatformChangeDetailDrawer for full detail + side-by-
 *     side before/after state.
 *
 * Description column trims to 80 chars + ellipsis so the list stays
 * scannable; full text lives in the drawer.
 */
import { useState, type JSX } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { ClipboardList, Search } from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import { PlatformChangeDetailDrawer } from '../../components/platform/PlatformChangeDetailDrawer.js'
import {
  usePlatformChangeLog,
  type ChangeEntry,
  type ChangeFilters,
} from '../../hooks/usePlatformChangeLog.js'
import { formatDateTime } from '../../utils/formatDate.js'

const MAX_DESC_LEN = 80

function truncate(s: string, n: number): string {
  if (s.length <= n) return s
  return `${s.slice(0, n)}…`
}

function shortId(s: string | null): string {
  if (!s) return '—'
  return s.slice(0, 8)
}

export default function PlatformChangeLogPage(): JSX.Element {
  return (
    <RequirePermission permission="platform:audit_view">
      <PlatformChangeLogPageInner />
    </RequirePermission>
  )
}

function PlatformChangeLogPageInner(): JSX.Element {
  const [filters, setFilters] = useState<ChangeFilters>({})
  const { rows, loading, error, loadNextPage } = usePlatformChangeLog(filters)
  const [selected, setSelected] = useState<ChangeEntry | null>(null)

  function setField<K extends keyof ChangeFilters>(
    key: K,
    next: ChangeFilters[K] | undefined,
  ): void {
    const cleaned: ChangeFilters = {}
    if (filters.changeType !== undefined) cleaned.changeType = filters.changeType
    if (filters.requestedBy !== undefined) cleaned.requestedBy = filters.requestedBy
    if (filters.approvedBy !== undefined) cleaned.approvedBy = filters.approvedBy
    if (filters.from !== undefined) cleaned.from = filters.from
    if (filters.to !== undefined) cleaned.to = filters.to
    if (next === undefined || next === '' || next === null) {
      delete cleaned[key]
    } else {
      cleaned[key] = next
    }
    setFilters(cleaned)
  }

  return (
    <div className="w-full space-y-5 px-6 py-6 pb-10">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
          <ClipboardList className="h-5 w-5 text-[#3454cf]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Change Management Log</h1>
          <p className="text-sm text-gray-400 mt-0.5">SOX-ITGC change records with before/after state.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Change type</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
              <input
                aria-label="Filter by change type"
                placeholder="e.g. config"
                value={filters.changeType ?? ''}
                onChange={(e) => setField('changeType', e.target.value || undefined)}
                className="h-10 pl-9 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 outline-none transition-all w-48"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Requested by</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
              <input
                aria-label="Filter by requestedBy"
                placeholder="Actor ID"
                value={filters.requestedBy ?? ''}
                onChange={(e) => setField('requestedBy', e.target.value || undefined)}
                className="h-10 pl-9 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 outline-none transition-all w-48"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Approved by</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
              <input
                aria-label="Filter by approvedBy"
                placeholder="Actor ID"
                value={filters.approvedBy ?? ''}
                onChange={(e) => setField('approvedBy', e.target.value || undefined)}
                className="h-10 pl-9 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 outline-none transition-all w-48"
              />
            </div>
          </div>
        </div>
      </div>

      {error !== null && (
        <div role="alert" className="rounded-xl bg-red-50 border border-red-100 px-5 py-3 text-sm text-red-600 font-medium">
          {error}
        </div>
      )}

      <DataTable
        value={rows}
        dataKey="id"
        scrollable
        scrollHeight="flex"
        loading={loading}
        stripedRows
        className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
        virtualScrollerOptions={{
          itemSize: 44,
          lazy: true,
          onLazyLoad: () => { void loadNextPage() },
        }}
        onRowClick={(e) => setSelected(e.data as ChangeEntry)}
        emptyMessage="No change records."
      >
        <Column
          field="implementedAt"
          header="Implemented"
          body={(r: ChangeEntry) => <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(r.implementedAt)}</span>}
        />
        <Column
          field="changeType"
          header="Type"
          body={(r: ChangeEntry) => <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-[#3454cf]/5 text-[#3454cf]">{r.changeType}</span>}
        />
        <Column
          header="Description"
          body={(r: ChangeEntry) => <span className="text-sm text-gray-700">{truncate(r.description, MAX_DESC_LEN)}</span>}
        />
        <Column
          header="Requested by"
          body={(r: ChangeEntry) => <span className="text-xs font-mono text-gray-500">{shortId(r.requestedBy)}</span>}
        />
        <Column
          header="Approved by"
          body={(r: ChangeEntry) => <span className="text-xs font-mono text-gray-500">{shortId(r.approvedBy)}</span>}
        />
      </DataTable>

      <PlatformChangeDetailDrawer
        entry={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}
